import { MutationCache, QueryCache, QueryClient } from "@tanstack/react-query";
import { captureUnexpectedAppException } from "~/lib/sentry";
import {
  startUserAction,
  type UserActionObservation,
} from "~/lib/user-action-observer";
import { readUserActionMutationMeta } from "~/lib/user-actions";
import { sanitizeTelemetryContextValue } from "~/lib/telemetry/privacy";

function stringifyQueryKey(queryKey: readonly unknown[]): string {
  try {
    return JSON.stringify(sanitizeTelemetryContextValue(queryKey));
  } catch {
    return "[unserializable-query-key]";
  }
}

export function createAppQueryClient(): QueryClient {
  const observedUserActions = new WeakMap<object, UserActionObservation>();

  return new QueryClient({
    defaultOptions: {
      queries: {
        refetchOnReconnect: true,
        refetchOnWindowFocus: true,
        // App-wide freshness floor. ~230 queries already declared 30-60s
        // staleTimes; without a default, any query that forgets one refetches
        // on every window focus (default staleTime is 0). Override per-query
        // where data must be hotter (e.g. execution status polls).
        staleTime: 30_000,
      },
    },
    mutationCache: new MutationCache({
      onMutate(_variables, mutation) {
        const action = readUserActionMutationMeta(mutation.options.meta);
        if (action) {
          observedUserActions.set(mutation, startUserAction(action));
        }
      },
      onError(error, _variables, _context, mutation) {
        const observation = observedUserActions.get(mutation);
        if (observation) {
          observedUserActions.delete(mutation);
          observation.fail(error, {
            extra: {
              mutationKey:
                mutation.options.mutationKey != null
                  ? stringifyQueryKey(mutation.options.mutationKey)
                  : undefined,
            },
          });
          return;
        }

        captureUnexpectedAppException(error, {
          extra: {
            mutationKey:
              mutation.options.mutationKey != null
                ? stringifyQueryKey(mutation.options.mutationKey)
                : undefined,
          },
          name: "react_query_mutation",
        });
      },
      onSettled(_data, error, _variables, _context, mutation) {
        if (error) {
          return;
        }

        const observation = observedUserActions.get(mutation);
        if (!observation) {
          return;
        }

        observedUserActions.delete(mutation);
        observation.succeed({
          mutationKey:
            mutation.options.mutationKey != null
              ? stringifyQueryKey(mutation.options.mutationKey)
              : undefined,
        });
      },
    }),
    queryCache: new QueryCache({
      onError(error, query) {
        captureUnexpectedAppException(error, {
          extra: {
            queryHash: query.queryHash,
            queryKey: stringifyQueryKey(query.queryKey),
          },
          name: "react_query_query",
        });
      },
    }),
  });
}

export const queryClient = createAppQueryClient();

export type AppQueryClient = QueryClient;
